import React, { useState, useEffect,useContext } from 'react';
import { useSelector, useDispatch } from 'react-redux'
import {
    Box,
    Typography,
    Paper,
    Grid,
    TextField
} from '@mui/material';
import * as actions from '../../../../../store/actions/index'
import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from "react-i18next";
import ToolboxHeader from './ToolboxHeader';
import Toolbox from './Toolbox';
import DraggableField from './DraggableField';
import DraggableRow from './DraggableRow';
import AlertContext from '../../../../../context/AlertModelContext/AlertModalContext'
// const FIELD_OPTIONS = [
//     { id: 'first_name', label: 'שם פרטי' },
//     { id: 'last_name', label: 'שם משפחה' },
//     { id: 'phone', label: 'טלפון' },
// ];

const LayoutEditor = () => {
    const [sections, setSections] = useState([]);
    const [allAvailableFields, setAllAvailableFields] = useState([]);
    const [systemSectionOriginalId, setSystemSectionOriginalId] = useState(null);
    const [t] = useTranslation('crm')
    const dispatch = useDispatch()
    const alertCtx = useContext(AlertContext)
    const crmState = useSelector(state => state.crm)
    const { selectedCrmModule } = crmState

    useEffect(() => {
        if (selectedCrmModule)
            loadSections(selectedCrmModule.sections)
    }, [selectedCrmModule])

    const loadSections = (rawSections) => {
        // שלב 1: שמירת ה־_id של systemSection (אם קיים)
        const systemSection = rawSections.find((s) => s.name === 'systemSection');
        const systemSectionId = systemSection?._id;
        setSystemSectionOriginalId(systemSectionId || null);

        // שלב 2: שמירת כל השדות (כולל systemSection)
        const allFields = rawSections.flatMap((section) => section.fields || []);
        setAllAvailableFields(allFields);

        // שלב 3: התעלמות מ־systemSection בפריסה הראשית
        const sectionsToRender = rawSections.filter((s) => s.name !== 'systemSection');

        const structured = sectionsToRender.map((section, sectionIndex) => {
            const updatedLabel = section.label?.trim() || `מקטע ${sectionIndex + 1}`;
            const updatedName = section.name || `section_${sectionIndex}`;
            const originalId = section._id;
        
            const orderedFields = [...(section.fields || [])]
                .filter(f => f.order != null)
                .sort((a, b) => a.order - b.order);
        
            const rowsMap = new Map();
        
            orderedFields.forEach((field) => {
                const orderIndex = field.order - 1;
                const rowIndex = Math.floor(orderIndex / 2);
                const colIndex = orderIndex % 2;
        
                const rtlColIndex = colIndex === 0 ? 1 : 0;
        
                if (!rowsMap.has(rowIndex)) {
                    rowsMap.set(rowIndex, {
                        id: uuidv4(),
                        fields: [null, null],
                    });
                }
        
                const row = rowsMap.get(rowIndex);
                row.fields[rtlColIndex] = {
                    ...field,
                    id: uuidv4(),
                    originalId: field._id,
                };
            });
        
            // ממירים את המפה למערך, לפי הסדר
            const rows = Array.from(rowsMap.entries())
                .sort((a, b) => a[0] - b[0])
                .map(([, row]) => row);
        
            return {
                id: uuidv4(),
                originalId,
                name: updatedName,
                label: updatedLabel,
                rows,
                isEditingName: false,
            };
        });

        setSections(structured);
    };

    const updateSectionName = (id, newName) => {
        setSections((prev) =>
            prev.map((section) =>
                section.id === id
                    ? {
                        ...section,
                        name: newName,
                        label: newName, // 💥 תואם גם את label
                    }
                    : section
            )
        );
    };

    const saveSectionName = (id) => {
        setSections((prev) =>
            prev.map((s) => (s.id === id ? { ...s, isEditingName: false } : s))
        );
    };

    const editSectionName = (id) => {
        setSections((prev) =>
            prev.map((s) => (s.id === id ? { ...s, isEditingName: true } : s))
        );
    };

    const addSection = (index = null) => {
        const newSection = {
            id: uuidv4(),
            rows: [],
            name: '',
            isEditingName: true,
        };
        setSections((prev) => {
            const copy = [...prev];
            if (index === null || index >= copy.length) {
                copy.push(newSection);
            } else {
                copy.splice(index, 0, newSection);
            }
            return copy;
        });
    };

    const addRowToSection = (sectionId, rowIndex = null) => {
        setSections((prev) =>
            prev.map((s) => {
                if (s.id !== sectionId) return s;
                const newRow = {
                    id: uuidv4(),
                    fields: [null, null],
                };
                const rows = [...s.rows];
                if (rowIndex === null || rowIndex >= rows.length) {
                    rows.push(newRow);
                } else {
                    rows.splice(rowIndex, 0, newRow);
                }
                return { ...s, rows };
            })
        );
    };

    const handleDeleteDrop = (e) => {
        e.preventDefault();

        const id = e.dataTransfer.getData('id');
        const type = e.dataTransfer.getData('type');

        setSections((prev) => {
            const updated = JSON.parse(JSON.stringify(prev));

            if (type === 'section') {
                // מחיקת מקטע שלם
                return updated.filter((section) => section.id !== id);
            }

            if (type === 'row') {
                // מחיקת שורה ממקטעים
                return updated.map((section) => ({
                    ...section,
                    rows: section.rows.filter((row) => row.id !== id),
                }));
            }

            if (type === 'field') {
                // מחיקת שדה – לפי id או originalId
                return updated.map((section) => ({
                    ...section,
                    rows: section.rows.map((row) => ({
                        ...row,
                        fields: row.fields.map((f) =>
                            f && (f.id === id || f.originalId === id) ? null : f
                        ),
                    })),
                }));
            }

            return prev; // אם לא זיהינו סוג – לא משנים
        });
    };

    const handleFieldDrop = (e, sectionId, rowId, index) => {
        e.preventDefault();

        const id = e.dataTransfer.getData('id');
        const from = e.dataTransfer.getData('from');

        setSections((prev) => {
            const updated = JSON.parse(JSON.stringify(prev));

            // מחפש שדה קיים ומוציא אותו
            let draggedField = null;

            for (const section of updated) {
                for (const row of section.rows) {
                    for (let i = 0; i < row.fields.length; i++) {
                        const f = row.fields[i];
                        if (f && f.originalId === id) {
                            draggedField = f;
                            row.fields[i] = null;
                        }
                    }
                }
            }

            // אם לא נמצא בפריסה - יוצרים אותו מהטולבוקס
            if (!draggedField && from === 'toolbox') {
                const original = allAvailableFields.find((f) => f._id === id);
                if (!original) return prev;
                draggedField = {
                    ...original,
                    id: uuidv4(),
                    originalId: original._id,
                };
            }

            // שמים במקום החדש
            const targetSection = updated.find((s) => s.id === sectionId);
            const targetRow = targetSection?.rows.find((r) => r.id === rowId);
            if (targetRow) {
                targetRow.fields[index] = draggedField;
            }

            return updated;
        });
    };


    const handleRowDrop = (e, sectionId, targetRowIndex) => {
        e.preventDefault();
        const type = e.dataTransfer.getData('type');

        if (type === 'new-row') {
            addRowToSection(sectionId, targetRowIndex);
            return;
        }

        const draggedRowId = e.dataTransfer.getData('id');
        if (type !== 'row') return;

        setSections((prev) => {
            let draggedRow;
            const newSections = prev.map((section) => {
                const rows = section.rows.filter((row) => {
                    if (row.id === draggedRowId) {
                        draggedRow = row;
                        return false;
                    }
                    return true;
                });
                return { ...section, rows };
            });

            return newSections.map((section) => {
                if (section.id !== sectionId) return section;
                const rows = [...section.rows];
                rows.splice(targetRowIndex, 0, draggedRow);
                return { ...section, rows };
            });
        });
    };

    const handleLayoutDrop = (e) => {
        e.preventDefault();
        const type = e.dataTransfer.getData('type');
        if (type === 'new-section') {
            addSection();
        }
    };
    const handleSectionDrop = (e, index) => {
        e.preventDefault();
        const type = e.dataTransfer.getData('type');
        const draggedSectionId = e.dataTransfer.getData('id');

        if (type !== 'section') return;

        setSections((prev) => {
            const draggedSection = prev.find((s) => s.id === draggedSectionId);
            const filtered = prev.filter((s) => s.id !== draggedSectionId);
            const updated = [...filtered];
            updated.splice(index, 0, draggedSection);
            return updated;
        });
    };

    const usedFieldIds = sections.flatMap((s) =>
        s.rows.flatMap((r) =>
            r.fields.map(f => f?.originalId).filter(Boolean)
        )
    );
    const availableFields = allAvailableFields; // תמיד מציגים את כולם

    const prepareSectionsForSave = (sections, allAvailableFields, systemSectionOriginalId) => {
        let globalOrder = 1;
        const result = [];
        const usedFieldIds = new Set();

        sections.forEach((section, sectionIndex) => {
            const fieldsInSection = [];

            section.rows.forEach((row) => {
                [1, 0].forEach((i) => {
                    const f = row.fields[i];
                    if (f && f.originalId) {
                        const original = allAvailableFields.find(of => of._id === f.originalId);
                        if (original) {
                            fieldsInSection.push({
                                ...original,
                                order: globalOrder++,
                            });
                            usedFieldIds.add(original._id);
                        } else {
                            globalOrder++;
                        }
                    } else {
                        globalOrder++;
                    }
                });
            });

            const builtSection = {
                name: section.name?.trim() || `מקטע ${sectionIndex + 1}`,
                label: section.label?.trim() || `מקטע ${sectionIndex + 1}`,
                order: sectionIndex + 1,
                fields: fieldsInSection,
            };

            if (section.originalId || section._id) {
                builtSection._id = section.originalId || section._id;
            }

            result.push(builtSection);
        });

        // 🟡 הוספת systemSection עם ה־_id אם קיים
        const unusedFields = allAvailableFields.filter(f => !usedFieldIds.has(f._id));
        if (unusedFields.length > 0) {
            const systemSection = {
                name: 'systemSection',
                label: 'systemSection',
                order: null,
                fields: unusedFields.map((f) => ({
                    ...f,
                    order: null,
                })),
            };

            if (systemSectionOriginalId) {
                systemSection._id = systemSectionOriginalId; // 💥 מוסיף את ה־_id מהשרת
            }

            result.push(systemSection);
        }

        return result;
    };



    const handleSave = () => {
        setSections((prevSections) => {
            return prevSections.map((section, index) => {
                const hasCustomName = !!section.name?.trim();

                const fallbackName = `מקטע ${index + 1}`;
                const fallbackLabel = `מקטע ${index + 1}`;

                const finalName = hasCustomName ? section.name.trim() : fallbackName;
                const finalLabel = hasCustomName ? section.label?.trim() || finalName : fallbackLabel;

                return {
                    ...section,
                    name: finalName,
                    label: finalLabel,
                    isEditingName: false,
                };
            });
        });

        setTimeout(() => {
            const newSections = prepareSectionsForSave(sections, allAvailableFields, systemSectionOriginalId);
            const updatedModule = JSON.parse(JSON.stringify(selectedCrmModule))
            updatedModule.sections = newSections
            dispatch(actions.updateCrmModule(updatedModule))
            // alertCtx.showAlert(t('Action succeeded'), t('New Layout saved successfuly'), 'md')
        }, 0);
    };

    return (
        <Grid
            container
            spacing={2}
            sx={{
                height: '80vh',
                overflow: 'hidden',
                direction: 'rtl', // אם אתה לא כבר משתמש
            }}
        >
            <Grid item xs={12} >
                <ToolboxHeader
                    onSave={() => {
                        handleSave()
                    }}
                    onRestore={() => { loadSections(selectedCrmModule.sections) }}
                />
            </Grid>
            <Grid item sx={{
                // maxHeight: 'calc(70vh)'
                height: '80%'
            }}>
                <Toolbox onDeleteDrop={handleDeleteDrop} />
            </Grid>

            <Grid item xs sx={{ height: '80%', overflowY: 'auto' }}>
                <Box
                    sx={{
                        // maxHeight: 'calc(70vh)',
                        // overflowY: 'auto',

                    }}
                >
                    <Paper
                        sx={{ p: 2, height: '100%' }}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={handleLayoutDrop}
                    >
                        <Typography variant="h5">{t('Fields Layout')}</Typography>
                        <Box mt={2}>
                            {sections.map((section, idx) => (
                                <Box
                                    key={section.id}
                                    data-index={idx}
                                    sx={{
                                        border: '1px solid #ccc',
                                        borderRadius: 2,
                                        p: 1.25, // היה 2
                                        mb: 1.5, // היה 2
                                        transition: 'background-color 0.2s',
                                        backgroundColor: '#fff',
                                        cursor: 'grab',
                                        '&:hover': {
                                            backgroundColor: '#e2e3e5',
                                        },
                                    }}
                                    draggable
                                    onDragStart={(e) => {
                                        e.dataTransfer.setData('type', 'section');
                                        e.dataTransfer.setData('id', section.id);
                                    }}
                                    onDrop={(e) => handleSectionDrop(e, idx)}
                                    onDragOver={(e) => e.preventDefault()}
                                >
                                    {section.isEditingName ? (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexDirection: 'row-reverse' }}>
                                            <TextField
                                                fullWidth
                                                dir="rtl"
                                                variant="outlined"
                                                size="small"
                                                label={t('Section name')}
                                                value={section.name || ''}
                                                onChange={(e) => updateSectionName(section.id, e.target.value)}
                                            />
                                            <Box
                                                component="button"
                                                onClick={() => saveSectionName(section.id)}
                                                sx={{
                                                    bgcolor: '#f0f0f0',
                                                    color: '#333',
                                                    border: '1px solid #ccc',
                                                    borderRadius: '8px',
                                                    px: 2,
                                                    py: 0.5,
                                                    fontWeight: 500,
                                                    fontSize: '0.875rem',
                                                    cursor: 'pointer',
                                                    transition: '0.2s',
                                                    '&:hover': {
                                                        bgcolor: '#e0e0e0',
                                                        borderColor: '#bbb'
                                                    },
                                                }}
                                            >
                                                {t('Save')}
                                            </Box>
                                        </Box>
                                    ) : (
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, px: 1, flexDirection: 'row-reverse' }}>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>{section.name || t('Section x', { x: idx + 1 })}</Typography>
                                            <Box
                                                component="button"
                                                onClick={() => editSectionName(section.id)}
                                                sx={{
                                                    bgcolor: '#f0f0f0',
                                                    border: '1px solid #ccc',
                                                    borderRadius: '8px',
                                                    px: 2,
                                                    py: 0.5,
                                                    cursor: 'pointer',
                                                    fontSize: '0.85rem',
                                                    color: '#333',
                                                    transition: '0.2s',
                                                    '&:hover': { bgcolor: '#e0e0e0', borderColor: '#bbb' },
                                                }}
                                            >
                                                {t('Change')}
                                            </Box>
                                        </Box>
                                    )}

                                    {section.rows.map((row, rowIdx) => (
                                        <DraggableRow
                                            key={row.id}
                                            row={row}
                                            onDragStart={(e) => {
                                                e.stopPropagation();
                                                e.dataTransfer.setData('type', 'row');
                                                e.dataTransfer.setData('id', row.id);
                                            }}
                                        >
                                            <Box
                                                onDragOver={(e) => e.preventDefault()}
                                                onDrop={(e) => handleRowDrop(e, section.id, rowIdx)}
                                                sx={{ display: 'flex', gap: 2, mt: 1 }}
                                            >
                                                {row.fields.map((field, i) => (
                                                    <DraggableField
                                                        key={i}
                                                        field={field}
                                                        onDragStart={(e) => {
                                                            e.stopPropagation();
                                                            if (!field) return;
                                                            e.dataTransfer.setData('type', 'field');
                                                            e.dataTransfer.setData('id', field._id); // המקורי
                                                            e.dataTransfer.setData('from', 'toolbox'); // חשוב!
                                                        }}
                                                        onDrop={(e) => handleFieldDrop(e, section.id, row.id, i)}
                                                    />
                                                ))}
                                            </Box>
                                        </DraggableRow>
                                    ))}

                                    <Box
                                        onDragOver={(e) => e.preventDefault()}
                                        onDrop={(e) => handleRowDrop(e, section.id, section.rows.length)}
                                        sx={{ mt: 2, border: '2px dashed #ccc', p: 1, textAlign: 'center', cursor: 'pointer' }}
                                    >
                                        {t('Drag here to add a line')}
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    </Paper>
                </Box>
            </Grid>

            <Grid item sx={{ height: '80%' }}>
                <Paper sx={{
                    p: 2,
                    width: 300,
                    // maxHeight: 'calc(70vh)',
                    height: '100%',
                    overflowY: 'auto',
                    bgcolor: '#fff',
                }}>
                    <Typography variant="h6">שדות</Typography>
                    <Box mt={2}>
                        {availableFields.map((field) => {
                            const isUsed = usedFieldIds.includes(field._id);
                            return (
                                <Box
                                    key={field._id}
                                    sx={{
                                        p: 1,
                                        bgcolor: isUsed ? '#e0e0e0' : '#fff',
                                        border: '1px solid #ccc',
                                        borderRadius: 1,
                                        mb: 1,
                                        cursor: isUsed ? 'not-allowed' : 'grab',
                                        opacity: isUsed ? 0.4 : 1
                                    }}
                                    draggable={!isUsed}
                                    onDragStart={(e) => {
                                        if (isUsed) return;
                                        e.dataTransfer.setData('type', 'field');
                                        e.dataTransfer.setData('id', field._id);
                                        e.dataTransfer.setData('from', 'toolbox');
                                    }}
                                >
                                    {field.label}
                                </Box>
                            );
                        })}
                    </Box>
                </Paper>
            </Grid>
        </Grid>
    );
};

export default LayoutEditor;
